import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import ImageWIthSignedUrl from "components/S3Utility/ImageWIthSignedUrl";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from "react-zoom-pan-pinch";
import authHeader from "services/auth-header";
import API from "services/axios";
import WoArtworkImgCrud from "../WorkOrders/WorkOrderDetails/LotApproval/WoArtworkImgCrud";

export default function ApprovalProducts() {
  const dispatch = useDispatch();
  /*   ALL STATES
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });
  const [approvalProductsData, setApprovalProductsData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ path: "", alt: "" });

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  //get Data (uses POST /approval-products?page=&limit=)
  const getApprovalProductsData = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      let _query = searchTerm ? searchTerm : query;

      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof _query !== "undefined"
          ? _query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/approval-products?page=" + usedPage + "&limit=" + pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          // API returns { success, data, total, page, limit, totalPages }
          setApprovalProductsData(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, pageLimit, searchTerm, filterBy.value]
  );

  const Controls = () => {
    const { zoomIn, zoomOut, resetTransform } = useControls();

    return (
      <div className="image-controls ms-3 d-flex align-items-center gap-2">
        <button
          className="control-btn zoom-btn"
          onClick={() => zoomIn()}
          aria-label="Zoom In"
        >
          <i className="mdi mdi-plus"></i>
        </button>
        <button
          className="control-btn zoom-btn"
          onClick={() => zoomOut()}
          aria-label="Zoom Out"
        >
          <i className="mdi mdi-minus"></i>
        </button>
        <button
          className="control-btn reset-btn"
          onClick={() => resetTransform()}
          aria-label="Reset Zoom"
        >
          <i className="mdi mdi-refresh"></i>
        </button>
      </div>
    );
  };

  const handleImageDoubleClick = (imagePath, productDescription) => {
    setSelectedImage({ path: imagePath, alt: productDescription });
    setShowImageModal(true);
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getApprovalProductsData();
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit]);

  /*   Table functions
   ********************************************* */

  const table_head = [
    { name: "W-ID", value: "approvalProductsDataId" },
    { name: "Product Img", value: "productImg" },
    { name: "Product type", value: "productType" },
    { name: "Product Style", value: "productStyle" },
    { name: "Product Color", value: "productColor" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="" key={item.id}>
      <td className="align-middle py-1">W-{item?.WorkOrder?.woId}</td>
      <td className="align-middle py-1">
        {item?.productImage ? (
          <div
            onDoubleClick={() =>
              handleImageDoubleClick(
                item?.productImage,
                item?.productDescription
              )
            }
            style={{ cursor: "pointer" }}
          >
            <ImageWIthSignedUrl
              path={item?.productImage}
              alt={item?.productDescription}
              imgClasses={"rounded-3"}
              imgStyle={{
                width: "50px",
                height: "30px",
              }}
            />
          </div>
        ) : (
          <div
            className="rounded border border-3 d-flex align-items-center justify-content-center"
            style={{
              width: "50px",
              height: "30px",
            }}
          >
            <i
              className="fas fa-image text-black text-muted"
              style={{ fontSize: "12px" }}
            >
              N/A
            </i>
          </div>
        )}
      </td>
      <td className="align-middle py-1">{item.productType}</td>
      <td className="align-middle py-1">{item.productStyle}</td>
      <td className="align-middle py-1">{item.productColor}</td>
      <td className="align-middle py-1 text-end table-action">
        {/* <button
          disabled={loading}
          onClick={() => {
            handleUpdateData(item);
          }}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button> */}
        {item && (
          <WoArtworkImgCrud
            product={item}
            handleImageDoubleClick={handleImageDoubleClick}
            refreshFunc={getApprovalProductsData}
          />
        )}
      </td>
    </tr>
  );

  return (
    <div className="p-2" style={{ marginLeft: 260, height: "100vh" }}>
      <h2 className="mb-3 ps-2">Approval Products</h2>
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {/* {buttons()} */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getApprovalProductsData({ query: searchTerm, page: 1 });
              } else {
                getApprovalProductsData({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getApprovalProductsData({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {/* Add button moved next to search for consistent layout */}
          {/* <div className="">
            <button
              onClick={() => {
                setShowAddModal(true);
                setFormData({});
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add ApprovalProducts
            </button>
          </div> */}
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={approvalProductsData}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      {/* Image Preview Modal */}
      <Modal show={showImageModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <div className="image-viewer-container">
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={8}
                centerOnInit={true}
                centerZoomedOut={true}
                doubleClick={{ mode: "reset" }}
              >
                {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                  <div
                    style={{
                      position: "relative",
                    }}
                  >
                    <TransformComponent wrapperClass="" contentClass="">
                      <ImageWIthSignedUrl
                        path={selectedImage.path}
                        imgStyle={{
                          height: "600px",
                          width: "auto",
                          objectFit: "contain",
                        }}
                        imgClasses="zoomable-image bg-light border rounded-3"
                      />
                    </TransformComponent>
                    <div className="zoom-tip mt-2 text-muted d-flex  text-center justify-content-center align-items-center">
                      <h5>Tip: Double-click to reset view</h5>
                      <Controls />
                    </div>
                  </div>
                )}
              </TransformWrapper>
            </div>
          ) : (
            <div className="no-image-placeholder">
              <div className="placeholder-icon">🖼️</div>
              <p>No image available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleCloseModal}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/*   Add Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>
              {isUpdateMode
                ? "Update ApprovalProducts"
                : "Add ApprovalProducts"}
            </h4>
          </div>
          <hr className="mt-0" />
          <form
            // onSubmit={
            //   isUpdateMode
            //     ? handleUpdateApprovalProductsData
            //     : handleApprovalProductsData
            // }
            className=""
          >
            <div className="mb-1">
              <label className="">Data Name</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.name || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Data Name"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Contact Name</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.contactName || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, contactName: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Contact Name"
                />
              </div>
            </div>
            <div className="mb-1">
              <label className="">Contact Number</label>
              <div className="">
                <input
                  type="text"
                  value={formData?.contactNumber || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, contactNumber: e.target.value });
                  }}
                  className="form-control"
                  placeholder="Enter Contact Number"
                />
              </div>
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
